import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from "@angular/core";
import {
    AddAssigneeTaskActionDTO,
    AddLabelTaskActionDTO,
    ArchiveTaskActionDTO,
    Task,
    TaskActionDTO,
    TaskAssignee,
    TaskLabel,
    TaskListItem
} from "../../taskbook-triage/taskbook.types";
import { TaskbookService } from "../../taskbook-triage/taskbook.service";
import { FormatedSavedFilter } from "@teammood/filter";
import { catchError, debounceTime, distinctUntilChanged, EMPTY, tap } from "rxjs";
import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { LoggerService } from "@teammood/shared/util/logger";
import { FormControl } from "@angular/forms";
import { BacklogListService, TaskLabelForSearch } from "./backlog-list.service";
import { TranslateService } from "@ngx-translate/core";
import { UserInfos } from "../../shared/ui/username-icon/user-infos.type";
import { compareAsc } from 'date-fns';


@Component({
    selector: 'backlog-list',
    templateUrl: './backlog-list.component.html',
    styleUrls: ['./backlog-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
    providers: [
        BacklogListService
    ]
})
export class BacklogListComponent implements OnInit, OnChanges{

    protected filteredListItems: TaskListItem[];
    protected taskSelection: Task[] = [];
    protected selectedItemForDetails: TaskListItem;
    protected searchInputCtrl = new FormControl<string>('');
    protected search_terms: string = '';
    protected select_all_state: boolean;
    protected assigneeSelection: TaskAssignee[] = [];
    protected labelSelection: TaskLabel[] = [];
    protected availableLabels: TaskLabel[];
    protected labelSelectionForSearch: TaskLabelForSearch[];
    protected allLabel: TaskLabelForSearch = {id: 'all', name: this.translateService.instant('all_filter'), color: '', selected: true};
    protected displayTeamNames: boolean;

    @Input() listItems: TaskListItem[];
    @Input() managers: TaskAssignee[];
    @Input() userId: string;
    @Input() selectedFilter: FormatedSavedFilter;

    @Output()
    onSelectTaskForDetails: EventEmitter<TaskListItem> = new EventEmitter();

    @Output()
    onArchived: EventEmitter<TaskListItem> = new EventEmitter();

    constructor(
        private taskbookService: TaskbookService,
        private backlogListService: BacklogListService,
        private snackBarService: SnackBarService,
        private logger: LoggerService,
        private translateService: TranslateService
    ){}

    ngOnInit(): void {
        this.filteredListItems = this.listItems;

        this.searchInputCtrl.valueChanges.pipe(
            debounceTime(200),
            distinctUntilChanged(),
            tap(async () => {
                this.search_terms = this.searchInputCtrl.getRawValue();
                this.applySearch()
            }),
        ).subscribe();

        this.taskbookService.availableLabels().pipe(
            tap(availableLabels => {
                this.availableLabels = availableLabels;
                this.labelSelectionForSearch = this.backlogListService.getLabelsForSearch(this.availableLabels);
            }),
        ).subscribe();

    }

    ngOnChanges(changes: SimpleChanges): void {
        if(!changes.tasks?.isFirstChange()){
            this.applySearch()
        }
        if(changes.selectedFilter?.currentValue){
            this.displayTeamNames = ! this.selectedFilter.baseNamedFilter;
        }
    }

    getLabelCount(labelId: string) {
        if(labelId === "all") return this.listItems.length
        return this.filteredListItems.filter(task => task.task.labels.find(label => label.id === labelId)).length
    }

    applyTextSearch(){
        const search = this.search_terms.trim();
        this.filteredListItems = search === '' ? this.listItems : this.listItems.filter(task => this.fullTextPredicate(task, this.search_terms));
    }

    applyLabelSearch() {
        this.filteredListItems = this.backlogListService.applyLabelFilters(this.filteredListItems, this.labelSelectionForSearch);
    }

    applySearch() {
        this.applyTextSearch();
        this.applyLabelSearch();
    }

    onApplyLabelSelection(labels: TaskLabel[]) {
        this.labelSelection = labels;
        const actions: AddLabelTaskActionDTO[] = [];
        this.taskSelection.forEach(task => {
            const action = new AddLabelTaskActionDTO(task.id, task, labels, this.selectedFilter.decodedFilter);
            actions.push(action);
        });
        this.updateLabels(actions)
    }

    updateLabels(actions: TaskActionDTO[]) {
        this.taskbookService.saveAction(actions).pipe(
            tap((items: TaskListItem[]) => {
                this.listItems = items;
                this.labelSelection = [];
                this.select_all_state = false;
                this.applySearch();
                this.snackBarService.success('labels updated');
            }),
            catchError(error => {
                this.logger.error('Error saving action:', error?.text);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
        ).subscribe();
    }

    toggleLabel(labelId: string) {
        this.labelSelectionForSearch = this.backlogListService.toggleLabel(this.labelSelectionForSearch, labelId);
        this.applySearch();
    }


    private fullTextPredicate(item: TaskListItem, fullTextSearch: string = '') {
        const search = fullTextSearch.trim()
        return this.contains(item.listContent.body, search) || this.contains(item.task.assignees.map(assignee => assignee.firstname + ' ' + assignee.lastname).join(' '), search) || this.contains(item.task.itemType, search);
    }

    contains(value: string, term: string) {
        return value.toLowerCase().includes(term.toLowerCase())
    }

    clear_search() {
        this.searchInputCtrl.setValue('');
        this.search_terms = '';
        this.labelSelectionForSearch.forEach(label => {
            label.selected = label.id === "all" ? true : false;
            return label;
        })
        this.applySearch();
    }

    check(task){
        task.toggleSelection();
        this.updateTaskSelection();
    }

    updateTaskSelection() {
        const selectedItems = this.filteredListItems.filter(item => item.isSelected);
        this.taskSelection = selectedItems.map(item => item.task);
    }

    select_all(){
        if (this.filteredListItems) {
            this.filteredListItems = this.filteredListItems.map((item: TaskListItem) => {
                item.isSelected = !this.select_all_state;
                return item;
            });
            this.updateTaskSelection()
        }
    }

    selectItemForDetails(item: TaskListItem){
        this.selectedItemForDetails = item;
        this.onSelectTaskForDetails.emit(item);
    }

    getTaskReactions(item: TaskListItem){
        return this.taskbookService.getTaskReactions(item);
    }

    protected userInfos(assignee: TaskAssignee): UserInfos {
        return new UserInfos(assignee.firstname, assignee.lastname);
    }
    getTooltipAssigneesContent(assignees: TaskAssignee[]) {
        return assignees.slice(3).map(assignee => `${assignee.firstname} ${assignee.lastname}`).join(', ');
    }

    updateAssignees(actions: TaskActionDTO[]) {
        this.taskbookService.saveAction(actions).pipe(
            tap((items: TaskListItem[]) => {
                this.listItems = items;
                this.applySearch();
                this.select_all_state = false;
                this.snackBarService.success('assignees updated');
            }),
            catchError(error => {
                this.logger.error('Error saving action:', error?.text);
                this.snackBarService.error('Oops')
                return EMPTY;
            }),
        ).subscribe();
    }

    onApplyAssigneeSelection(assignees: TaskAssignee[]) {
        this.assigneeSelection = assignees;
        const actions: AddAssigneeTaskActionDTO[] = [];
        this.taskSelection.forEach(task => {
            const action = new AddAssigneeTaskActionDTO(task.id, task, assignees, this.selectedFilter.decodedFilter);
            actions.push(action);
        });
        this.updateAssignees(actions)
    }

    archive(item: TaskListItem){
        event.stopImmediatePropagation();
        const action: TaskActionDTO = new ArchiveTaskActionDTO(item.task.id, item.task.itemType, this.selectedFilter.decodedFilter);
        this.taskbookService.saveAction([action]).pipe(
            tap((items: TaskListItem[]) => {
                this.listItems = items;
                this.applySearch();
                this.snackBarService.success('item archived');
                this.onArchived.emit(item);
            }),
            catchError(error => {
                this.logger.error('Error saving action:', error?.text);
                this.snackBarService.error('Oops')
                return EMPTY;
            })
        ).subscribe();
    }

    sortBy(sortBy: SortDateType) {
        switch (sortBy) {
            case 'published-date-asc':
                this.filteredListItems = this.filteredListItems.sort((a, b) => compareAsc(new Date(a.task.date), new Date(b.task.date)));
                break;
            case 'published-date-desc':
                this.filteredListItems = this.filteredListItems.sort((a, b) => compareAsc(new Date(b.task.date), new Date(a.task.date)));
                break;
            case 'update-date-asc':
                this.filteredListItems = this.filteredListItems.sort((a, b) => compareAsc(new Date(a.getLastUpdate().date), new Date(b.getLastUpdate().date)));
                break;
            case 'update-date-desc':
                this.filteredListItems = this.filteredListItems.sort((a, b) => compareAsc(new Date(b.getLastUpdate().date), new Date(a.getLastUpdate().date)));
                break;
        }
    }
}


type SortDateType = 'published-date-desc' | 'published-date-asc'  | 'update-date-desc'  | 'update-date-asc';

